import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { endpoints } from "@/endpoints";
import { tags } from "@/api/common";
import useApiStore from "@/stores/api";
import { useLocationSelector } from "@/composables/location";
import type {
  AddLocationErrorResponse,
  AddLocationRequest,
  CreateGroupRequest,
  LocationGroup,
  LocationGroupsApiRequest,
  LocationGroupsApiResponse,
} from "@/api/locationGroups";
import { useUuid } from "@/composables/uuid";
import { queryKeys } from "../queryKeys";
import type { VisitorCategoryResponse } from "../visitorCategories";
import { cloneDeep } from "lodash";
import type { AgreementListApiResponse } from "../agreements";
import { useNotification } from "@/composables/useNotification";
import { useI18n } from "vue-i18n";

const mapFromLocationGroups = (
  input: Partial<
    Omit<LocationGroup, "locations" | "settings" | "numericId" | "id">
  >
): LocationGroupsApiRequest => {
  const settings: Partial<LocationGroupsApiRequest> = {};

  Object.entries(input).forEach(([k, value]) => {
    const key = k as keyof typeof input;
    switch (key) {
      // Booleans:
      case "invisible":
      case "visitor_auto_anonymise":
        settings[key] = value ? "1" : "0";
        break;
      default:
        settings[key] = value as string;
        break;
    }
  });

  return settings;
};

export const useUpdateLocationGroups = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const { selectedLocationGroupPk } = useLocationSelector();

  return useMutation(
    (settings: Partial<LocationGroup>) => {
      const payload: LocationGroupsApiRequest = mapFromLocationGroups(settings);

      return axios.patch(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.upsert(
            selectedLocationGroupPk.value as string
          )
        ),
        payload
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.locationGroups]._def
        );
        onSuccess?.();
      },
    }
  );
};

export const useAddLocationGroup = (
  onSuccess?: (newLocationPk: string) => void,
  onError?: (error: AxiosError<AddLocationErrorResponse>) => void
) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const { generateUuid } = useUuid();

  return useMutation<
    string,
    AxiosError<AddLocationErrorResponse>,
    AddLocationRequest
  >(
    async (payload) => {
      const response = await axios.put(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.upsert(generateUuid())
        ),
        payload
      );

      return response.data.locationPk;
    },

    {
      onSuccess: (newLocationPk) => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.locationGroups]._def
        );
        queryClient.invalidateQueries(
          queryKeys[tags.zoolander.subscription]._def
        );

        onSuccess?.(newLocationPk);
      },

      onError: (error) => {
        onError?.(error);
      },
    }
  );
};

export const useUpdateLocationGroupAgreement = () => {
  const apiStore = useApiStore();
  const queryClient = useQueryClient();
  const { selectedLocationGroupPk, selectedLocationPk } = useLocationSelector();
  const { sendErrorNotification } = useNotification();
  const { t } = useI18n();

  return useMutation(
    ({
      agreement,
      visitorCategory,
      fieldsPerCategoryEnabled,
    }: {
      agreement: string | null;
      visitorCategory?: string;
      fieldsPerCategoryEnabled?: boolean;
    }) => {
      const adjustedPayload =
        fieldsPerCategoryEnabled === true
          ? { agreement, visitorCategory }
          : { agreement };
      return axios.patch(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.agreements(
            selectedLocationGroupPk.value ?? ""
          )
        ),
        { ...adjustedPayload, locationPk: selectedLocationPk.value }
      );
    },
    {
      onMutate(variables) {
        const queryListKey = queryKeys[tags.platform.visitorCategories].list({
          locationGroupId: selectedLocationGroupPk,
        }).queryKey;

        queryClient.cancelQueries(queryListKey);

        const previousData =
          queryClient.getQueryData<VisitorCategoryResponse>(queryListKey);

        if (!previousData) return;

        const newData = cloneDeep(previousData);

        const categoryToUpdate = newData.list.find(
          (visitorCategory) => visitorCategory.pk === variables.visitorCategory
        );

        if (!categoryToUpdate) return;
        if (variables.agreement) {
          const agreements = queryClient.getQueryData<AgreementListApiResponse>(
            queryKeys[tags.platform.agreements].list().queryKey
          );

          const agreement = agreements?.data?.find(
            (agreement) => agreement.id === variables.agreement
          );
          if (agreement !== undefined && agreement.pdfPath !== undefined) {
            categoryToUpdate.agreements = [
              {
                id: agreement.id,
                name: agreement.name,
                type: agreement.type,
                pdfPath: agreement.pdfPath,
                createdAt: agreement.createdAt,
                updatedAt: agreement.updatedAt,
              },
            ];
          }
        } else {
          categoryToUpdate.agreements = [];
        }

        queryClient.setQueryData(queryListKey, newData);

        return { previousData, queryListKey };
      },
      onError: (_data, _variables, context) => {
        if (!context?.queryListKey) return;
        queryClient.setQueryData(context?.queryListKey, context.previousData);
        sendErrorNotification(
          t("agreementsLibrary.error.setAgreementAssignmentFailed"),
          true
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.locationGroups]._def
        );
        queryClient.invalidateQueries(
          queryKeys[tags.platform.visitorCategories]._def
        );
      },
    }
  );
};

export const useCreateGroup = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();

  const { generateUuid } = useUuid();
  const id = generateUuid();

  return useMutation(
    (payload: CreateGroupRequest) =>
      axios.put(
        apiStore.getEndpoint("platform", endpoints.locationGroups.upsert(id)),
        payload
      ),

    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.locationGroups]._def
        );
        onSuccess?.();
      },
    }
  );
};

export const useUpdateGroup = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();

  return useMutation(
    (request: LocationGroupsApiRequest) => {
      if (!request.id) {
        throw new Error("Location group ID missing");
      }
      return axios.patch(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.upsert(request.id)
        ),
        { name: request.name }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.locationGroups]._def
        );
        onSuccess?.();
      },
    }
  );
};

export const useDeleteGroup = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();

  return useMutation(
    (groupId: string) => {
      return axios.delete(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.delete(groupId)
        )
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          queryKeys[tags.platform.locationGroups]._def
        );
        onSuccess?.();
      },
    }
  );
};

export const useReorderLocationGroups = () => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const queryKey = queryKeys[tags.platform.locationGroups].all.queryKey;

  return useMutation(
    (payload: { locationGroupId: string; order: number }) => {
      return axios.patch(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.upsert(payload.locationGroupId)
        ),
        {
          order: payload.order,
        }
      );
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(queryKey);

        const previousData =
          queryClient.getQueryData<LocationGroupsApiResponse[]>(queryKey);

        return previousData;
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
      onError: (_data, _variables, previousData) => {
        queryClient.setQueryData(queryKey, previousData);
      },
    }
  );
};

export const useReorderLocation = () => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const queryKey = queryKeys[tags.platform.locationGroups].all.queryKey;

  return useMutation(
    (payload: { locationId: string; order: number }) => {
      return axios.patch(
        apiStore.getEndpoint(
          "platform",
          endpoints.locations.update(payload.locationId)
        ),
        {
          order: payload.order,
        }
      );
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(queryKey);

        const previousData =
          queryClient.getQueryData<LocationGroupsApiResponse[]>(queryKey);

        return previousData;
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
      onError: (_data, _variables, previousData) => {
        queryClient.setQueryData(queryKey, previousData);
      },
    }
  );
};

export const useMoveLocationGroup = () => {
  const { generateUuid } = useUuid();
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const queryKey = queryKeys[tags.platform.locationGroups].all.queryKey;

  return useMutation(
    (payload: {
      location: { pk: string };
      order?: number;
      sourceLocationGroupPk?: string;
    }) => {
      return axios.put(
        apiStore.getEndpoint(
          "platform",
          endpoints.locationGroups.upsert(generateUuid())
        ),
        payload
      );
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(queryKey);

        const previousData =
          queryClient.getQueryData<LocationGroupsApiResponse[]>(queryKey);

        return previousData;
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
      onError: (_data, _variables, previousData) => {
        queryClient.setQueryData(queryKey, previousData);
      },
    }
  );
};

export const useMoveLocation = () => {
  const queryClient = useQueryClient();
  const apiStore = useApiStore();
  const queryKey = queryKeys[tags.platform.locationGroups].all.queryKey;

  return useMutation(
    (payload: {
      locationId: string;
      params: { locationGroupPk?: string; order: number };
    }) => {
      return axios.patch(
        apiStore.getEndpoint(
          "platform",
          endpoints.locations.update(payload.locationId)
        ),
        payload.params
      );
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries(queryKey);
        const previousData = queryClient.getQueryData<{
          list: LocationGroupsApiResponse[];
        }>(queryKey);

        return previousData;
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      },
      onError: (_data, _variables, previousData) => {
        queryClient.setQueryData(queryKey, previousData);
      },
    }
  );
};
